// Declarative dashboard layer on top of the analytics engine.
//
// A module becomes an analytics dashboard by declaring a DashboardConfig +
// providing a `Record<metricKey, Sample[]>` extractor. Everything below is the
// SHARED glue: it runs the engine per metric and returns render-ready results.
// No module specifics live here — Fitness (or any future module) only writes a
// config object and a rows→samples function.

import {
  aggregate,
  periodOverPeriod,
  project,
  computeStreak,
  type Bucket,
  type Granularity,
  type Reducer,
  type Sample,
  type Trend,
  type Projection,
  type StreakResult,
} from "./engine";

export interface MetricConfig {
  key: string;
  /** i18n key for the metric label. */
  labelKey: string;
  reducer: Reducer;
  /** chart/series color (hsl string, VYV palette). */
  color: string;
  /** i18n key for the unit suffix (optional). */
  unitKey?: string;
  chart?: "line" | "bar";
  /** show the projection card for this metric. */
  projectable?: boolean;
  /** this metric's sample timestamps drive the streak card. */
  streak?: boolean;
  /** value formatter for display (defaults to rounded integer). */
  format?: (v: number) => string;
  /** Surface missing periods separately from measured zero; opt-in per metric. */
  includeObservationCounts?: boolean;
}

export interface DashboardConfig {
  /** module id, e.g. "fitness" — namespaces nothing here, used by consumers. */
  moduleKey: string;
  titleKey: string;
  metrics: MetricConfig[];
  defaultGranularity: Granularity;
  /** trend comparison window per granularity (defaults day/week=7, month=30). */
  windowDaysByGranularity?: Partial<Record<Granularity, number>>;
}

export interface MetricResult {
  config: MetricConfig;
  buckets: Bucket[];
  trend: Trend;
  projection: Projection | null;
  streak: StreakResult | null;
}

const DEFAULT_WINDOW: Record<Granularity, number> = { day: 7, week: 7, month: 30 };

function windowDaysFor(cfg: DashboardConfig, g: Granularity): number {
  return cfg.windowDaysByGranularity?.[g] ?? DEFAULT_WINDOW[g];
}

/** Run the engine for a single metric at a given granularity. Pure. */
export function buildMetric(
  samples: Sample[],
  metric: MetricConfig,
  config: DashboardConfig,
  granularity: Granularity,
  now?: number,
): MetricResult {
  const buckets = aggregate(samples, { granularity, reducer: metric.reducer });
  const trend = periodOverPeriod(samples, {
    windowDays: windowDaysFor(config, granularity),
    reducer: metric.reducer,
    now,
    includeObservationCounts: metric.includeObservationCounts,
  });
  const projection = metric.projectable ? project(buckets.map((b) => b.value)) : null;
  const streak = metric.streak ? computeStreak(samples.map((s) => s.t), { now }) : null;
  return { config: metric, buckets, trend, projection, streak };
}

/** Run the engine for every metric in the config. Pure. */
export function buildDashboard(
  samplesByMetric: Record<string, Sample[]>,
  config: DashboardConfig,
  granularity: Granularity,
  now?: number,
): MetricResult[] {
  return config.metrics.map((m) =>
    buildMetric(samplesByMetric[m.key] ?? [], m, config, granularity, now),
  );
}
